
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SubscriptionPlans } from './SubscriptionPlans';
import { SubscriptionFeature, SubscriptionPlan } from '@/hooks/use-subscription';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan?: SubscriptionPlan;
  subscriptionFeatures: SubscriptionFeature[];
  onPlanSelect?: (plan: SubscriptionPlan) => void;
  isStudioOwner: boolean;
}

export function UpgradeModal({
  isOpen,
  onClose,
  currentPlan,
  subscriptionFeatures,
  onPlanSelect,
  isStudioOwner,
}: UpgradeModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<string>(isStudioOwner ? 'studio' : 'customer');
  const { toast } = useToast();

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
  };

  const handleUpgrade = () => {
    if (!selectedPlan) {
      toast({
        title: "Fehler",
        description: "Bitte wähle zuerst einen Plan aus",
        variant: "destructive",
      });
      return;
    }
    
    // Here we would normally redirect to Stripe checkout
    // For now, just show a toast and close the modal
    toast({
      title: "Upgrade angefordert",
      description: `Du wirst gleich zum Bezahlprozess für ${selectedPlan} weitergeleitet...`,
    });
    
    // For demo purposes, just log the upgrade request
    console.log(`Stripe checkout requested for plan: ${selectedPlan}`);
    
    if (onPlanSelect) {
      onPlanSelect(selectedPlan);
    }
    
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Wähle dein Abonnement</DialogTitle>
          <DialogDescription>
            Upgrade auf einen höheren Plan, um mehr Features freizuschalten.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="customer" disabled={isStudioOwner}>Privatkunden</TabsTrigger>
            <TabsTrigger value="studio">Studios & Anbieter</TabsTrigger>
          </TabsList>
          
          <TabsContent value="customer" className="pt-4">
            <SubscriptionPlans 
              plans={subscriptionFeatures}
              currentPlan={currentPlan}
              onSelectPlan={handleSelectPlan}
              showCustomerPlans={true}
            />
          </TabsContent>
          
          <TabsContent value="studio" className="pt-4">
            <SubscriptionPlans 
              plans={subscriptionFeatures}
              currentPlan={currentPlan}
              onSelectPlan={handleSelectPlan}
              showCustomerPlans={false}
            />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end space-x-2 mt-6">
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={handleUpgrade} disabled={!selectedPlan}>
            Upgrade durchführen
          </Button>
        </div>

        <div className="mt-4 text-sm text-muted-foreground">
          <p>
            * Alle Preise verstehen sich inkl. MwSt. Das Abonnement läuft monatlich und kann jederzeit gekündigt werden.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
